"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.createSocketIoAdapter = createSocketIoAdapter;
var _socket4 = require("socket.io-client");
var _constants = require("../../utils/constants");
// Wraps today's socket.io-based real-time transport behind the shared
// ChannelAdapter interface (see ../channelAdapter.js). connect() is only ever
// called once, from ConversationProvider's mount effect — this is what fixes
// the historical bug where `io(url)` was called directly in a component body
// and reconnected on every render.
function createSocketIoAdapter() {
  let _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    url = _ref.url,
    _ref$socketOptions = _ref.socketOptions,
    socketOptions = _ref$socketOptions === void 0 ? {} : _ref$socketOptions,
    providedSocket = _ref.socket;
  let socket = providedSocket !== null && providedSocket !== void 0 ? providedSocket : null;
  function ensureSocket() {
    if (!socket) {
      if (!url) {
        throw new Error("createSocketIoAdapter: a socket url is required");
      }
      socket = (0, _socket4.io)(url, socketOptions);
    }
    return socket;
  }
  function connect() {
    return ensureSocket();
  }
  function disconnect() {
    var _socket;
    (_socket = socket) === null || _socket === void 0 || _socket.disconnect();
    socket = null;
  }
  function joinRoom(roomId) {
    if (roomId) ensureSocket().emit(_constants.SOCKET_EVENTS.JOIN_CHAT, roomId);
  }
  function leaveRoom(roomId) {
    var _socket2;
    if (roomId) (_socket2 = socket) === null || _socket2 === void 0 || _socket2.emit(_constants.SOCKET_EVENTS.LEAVE_CHAT, roomId);
  }
  function sendMessage(payload) {
    ensureSocket().emit(_constants.SOCKET_EVENTS.SEND_MESSAGE, payload);
  }
  function emitTyping(info) {
    ensureSocket().emit(_constants.SOCKET_EVENTS.TYPING, info);
  }
  function emitStopTyping(info) {
    var _socket3;
    (_socket3 = socket) === null || _socket3 === void 0 || _socket3.emit(_constants.SOCKET_EVENTS.STOP_TYPING, info);
  }
  function emitRefreshAfterUpdateStatus(payload) {
    ensureSocket().emit(_constants.SOCKET_EVENTS.REFRESH_AFTER_UPDATE_STATUS, payload);
  }
  function emitReplaceRequireResponse(payload) {
    ensureSocket().emit(_constants.SOCKET_EVENTS.REPLACE_REQUIRE_RESPONSE, payload);
  }
  function emitDeleteChatMessage(payload) {
    ensureSocket().emit(_constants.SOCKET_EVENTS.DELETE_CHAT_MESSAGE, payload);
  }
  function emitReactToMessage(payload) {
    ensureSocket().emit(_constants.SOCKET_EVENTS.REACT_CHAT_MESSAGE, payload);
  }
  function on(eventName, handler) {
    const instance = ensureSocket();
    instance.on(eventName, handler);
    return () => instance.off(eventName, handler);
  }

  // Reserved for a future adapter (e.g. WooChat) whose upstream protocol may
  // support presence/read-receipts — the current backend has no such events,
  // so these are stable no-ops rather than missing methods.
  const onPresence = () => () => {};
  const onSeen = () => () => {};
  return {
    connect,
    disconnect,
    joinRoom,
    leaveRoom,
    sendMessage,
    emitTyping,
    emitStopTyping,
    emitRefreshAfterUpdateStatus,
    emitReplaceRequireResponse,
    emitDeleteChatMessage,
    emitReactToMessage,
    onMessage: handler => on(_constants.SOCKET_EVENTS.INCOMING_MESSAGE, handler),
    onMessageReplaced: handler => on(_constants.SOCKET_EVENTS.INCOMING_REPLACE_MESSAGE, handler),
    onMessageDeleted: handler => on(_constants.SOCKET_EVENTS.AFTER_DELETE_CHAT, handler),
    onMessageReacted: handler => on(_constants.SOCKET_EVENTS.AFTER_REACT_CHAT, handler),
    onRequireResponse: handler => on(_constants.SOCKET_EVENTS.INCOMING_REQUIRE_RESPONSE, handler),
    onRequireResponseClosed: handler => on(_constants.SOCKET_EVENTS.AFTER_CLOSE_REQUIRE_RESPONSE, handler),
    onTyping: handler => on(_constants.SOCKET_EVENTS.USER_TYPING, handler),
    onStopTyping: handler => on(_constants.SOCKET_EVENTS.USER_STOP_TYPING, handler),
    onPresence,
    onSeen,
    // Lazily ensures the socket exists rather than just returning the current
    // `socket` variable — child components can run their mount effects before
    // ConversationProvider's own connect() effect fires (React flushes child
    // effects before parent effects), so a non-ensuring getter could hand
    // back null on that first render.
    getRawSocket: () => ensureSocket()
  };
}